/**
 * WHAT COLOUR A PTA OR PTB CYCLE IS DRAWN IN — from the PTA/PTB palette, not from a planet.
 *
 * <h2>The defect this exists for</h2>
 *
 * Reported as "clicking PTB also renders PTA's bands". It does not — a clean load with only PTB
 * clicked produces 565 overlays, every one of them `cycle-band-short-*` with role `ptbVertical`
 * or `ptbHorizontal`, and no PTA anywhere in the state. The STATE was already isolated.
 *
 * <p>What was not isolated is the thing a reader identifies a band by. The cycle presets took
 * their colours from the PLANET each cycle follows, and the two tables disagreed about which
 * hue means what:
 *
 * <pre>
 *                 cycle presets (via the planet)      the PTA/PTB palette
 *   PTA  long     Venus    GREEN   rgba(76,175,80)    AMBER  #F59E0B
 *   PTB  short    Mercury  AMBER   rgba(230,126,0)    CYAN   #06B6D4
 * </pre>
 *
 * So a PTB run painted the chart in the exact hue that means PTA in the replay, in the CTS
 * pillars and everywhere else — and the projected range bands added either side of each channel
 * tripled how much of it there was. "PTB is drawing PTA" is the correct reading of that picture.
 *
 * <p>The planet table is NOT the place to fix it: `PLANET_BAND_COLORS` is shared with the
 * retrograde-band study, where Mercury's amber means Mercury. The cycles are the ones borrowing
 * a colour that was never about them.
 *
 * <h2>Derived from the palette, never typed beside it</h2>
 *
 * Both halves come from {@link PTA_COLOR} / {@link PTB_COLOR} through {@link rgbaFromHex}, so
 * there is no second hex to drift. A test asserts the hue a cycle draws is the hue its category
 * has everywhere else.
 */
import { PTA_COLOR, PTB_COLOR } from './simulatorPalette';

/** The horizontal band's fill. Mercury's old 0.28, which is the weight these were tuned at. */
export const CYCLE_FILL_ALPHA = 0.28;

/**
 * The VERTICAL pillar's fill, much lighter.
 *
 * <p>A pillar is full height, so it is read against every candle it crosses rather than against
 * a band of its own — at the horizontal band's weight it would wash the whole chart. Lighter
 * than the band for the same reason the generation stripe is denser than the cycle tint: the
 * relationship is what says which mark is which.
 */
export const CYCLE_PILLAR_ALPHA = 0.16;

/** `#rrggbb` (or `#rgb`) as `rgba(r, g, b, a)`, or null when it is not a hex colour. */
export function rgbaFromHex(hex, alpha) {
  if (typeof hex !== 'string') return null;
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let body = m[1];
  if (body.length === 3) body = body.split('').map(c => c + c).join('');
  const n = parseInt(body, 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** The base hue for a cycle key, from the one place PTA and PTB are declared. */
export const cycleHex = key => (key === 'long' ? PTA_COLOR : PTB_COLOR);

/**
 * A cycle's colours: the horizontal band's fill and stroke, and the vertical pillar's fill.
 *
 * <p>Keyed on the CYCLE, not on its planet. Which body a cycle follows is a property of the
 * study; which colour it is drawn in is a property of being PTA or PTB, and conflating the two
 * is what put PTB's bands in PTA's hue.
 *
 * @param {string} key 'long' for PTA, anything else for PTB
 */
export function cycleColors(key) {
  const hex = cycleHex(key);
  return {
    fill: rgbaFromHex(hex, CYCLE_FILL_ALPHA),
    // Full strength: the stroke is what marks the MEASURED band, so it is the one part of a
    // cycle that should be unmistakable at any zoom.
    stroke: hex,
    pillar: rgbaFromHex(hex, CYCLE_PILLAR_ALPHA),
  };
}

/* ===============================================================================================
 * CONTRAST: what to draw ON a surface that is already the cycle's own hue
 * ===============================================================================================
 */

/**
 * The ink for a mark that sits on a full-strength cycle plate or fill.
 *
 * <p>Near-black rather than pure black, and the same value the replay's entry vertical uses, so a
 * chart does not carry two blacks that differ by three values of blue.
 */
export const CYCLE_INK = '#0b0f14';

/** The other option. White is right on a dark plate and wrong on both of ours — see below. */
export const CYCLE_PAPER = '#ffffff';

/**
 * WCAG relative luminance of a `#rrggbb`, or null when it is not a hex colour.
 *
 * <p>Exported because the choice below is a MEASUREMENT and a test re-derives it rather than
 * asserting a hex somebody eyeballed.
 */
export function relativeLuminance(hex) {
  if (typeof hex !== 'string') return null;
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let body = m[1];
  if (body.length === 3) body = body.split('').map(c => c + c).join('');
  const n = parseInt(body, 16);
  /* eslint-disable no-bitwise */
  const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  /* eslint-enable no-bitwise */
  const lin = channels.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/** The WCAG contrast ratio between two hex colours, or null when either is not one. */
export function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The legible mark colour for a plate of the given hue — whichever of ink and paper contrasts more.
 *
 * <h2>Both of ours come out INK, and that is the finding rather than a coincidence</h2>
 *
 * The cycle icons used to be a WHITE stem and disc on a plate of `#1565c0` / `#c62828`, which are
 * dark enough for white to work. The band hues are not:
 *
 * <pre>
 *                     white on it     ink on it
 *   PTA  #F59E0B         2.15 : 1      8.68 : 1
 *   PTB  #06B6D4         2.45 : 1      7.63 : 1
 * </pre>
 *
 * <p>So repainting the plates to match the bands — which is what was asked for — would have taken
 * the mark on them from legible to unreadable if the mark had been left alone. The plate is the
 * exact band colour and the mark follows from it, computed rather than typed, so a later change to
 * either palette entry cannot leave a white glyph on a yellow square.
 */
export function markOn(hex) {
  const ink = contrastRatio(hex, CYCLE_INK);
  const paper = contrastRatio(hex, CYCLE_PAPER);
  if (ink === null || paper === null) return CYCLE_PAPER;
  return ink >= paper ? CYCLE_INK : CYCLE_PAPER;
}

/**
 * `#rrggbb` composited over an OPAQUE `#rrggbb` backdrop at the given alpha.
 *
 * <p>Exported because the handle's colour is chosen against a tint rather than against a colour
 * anyone typed, and a test that re-derives the choice has to be able to produce the same tint.
 */
export function compositeOver(hex, alpha, backdrop) {
  const fg = /^#([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  const bg = /^#([0-9a-f]{6})$/i.exec(String(backdrop || '').trim());
  if (!fg || !bg) return null;
  const chans = (m) => {
    const n = parseInt(m[1], 16);
    /* eslint-disable no-bitwise */
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    /* eslint-enable no-bitwise */
  };
  const f = chans(fg);
  const b = chans(bg);
  return `#${f.map((c, i) => Math.round(alpha * c + (1 - alpha) * b[i]))
    .map(c => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The two canvases a band can be drawn on.
 *
 * <p>⚠ These must equal the themes' `background.default`, and `themeCanvas.test.js` asserts it by
 * reading `themes.js`. A canvas constant that drifts from the theme would make every contrast
 * decision below a measurement against a background nothing is drawn on — and the symptom would
 * be a mark that is merely hard to see, which nobody reports as a bug.
 */
export const CANVAS_LIGHT = '#f9fafe';
export const CANVAS_DARK = '#0d1117';

/** The canvas for a MUI palette mode. Anything that is not 'dark' is treated as light. */
export const canvasFor = (mode) => (mode === 'dark' ? CANVAS_DARK : CANVAS_LIGHT);

/**
 * The simulator HANDLE's colours: the diamond a reader clicks to open a channel's trade card.
 *
 * <h2>The defect: the handle was the same hue as everything it sits on</h2>
 *
 * It took `stroke` — the band's own full-strength colour — and it is drawn ON the equator line,
 * which is that colour, INSIDE a channel filled with that colour at {@link CYCLE_FILL_ALPHA}, with
 * projection bands either side at the same alpha again. One hue among a great deal of that hue,
 * at nine pixels. "I cannot find the handle" is the correct reading of that picture.
 *
 * <h2>Identity in the RIM, visibility in the CORE</h2>
 *
 * The rim is the band's own hue and is what still says PTA or PTB at a glance; the core is
 * whichever of ink and paper stands out against the surface the mark is actually drawn on. Both
 * halves matter: a bare high-contrast diamond would be perfectly visible and belong to nothing.
 *
 * <h2>⚠ THE CORE FOLLOWS THE THEME, and the reason is the measurement that chose it</h2>
 *
 * This shipped as an unconditional INK core, on a measurement taken over a WHITE canvas.
 *
 * <p>That arithmetic INVERTS on charcoal, and not marginally — the ink core goes from the best
 * available choice to very nearly the worst. Every figure below is computed from the constants
 * in this file rather than eyeballed, and `cycleContrast.test.js` re-derives them:
 *
 * <pre>
 *   band fill (0.28) over the canvas      ink      paper     core
 *   PTA over #f9fafe   #f8e0ba           14.97     1.28      INK
 *   PTA over #0d1117   #4e3814            1.74    11.05      PAPER
 *   PTB over #f9fafe   #b5e7f2           14.35     1.34      INK
 *   PTB over #0d1117   #0b3f4c            1.67    11.48      PAPER
 * </pre>
 *
 * <p>An ink core on the dark canvas is **1.7 : 1**: the handle disappears into the band, which is
 * exactly the defect the ink was introduced to fix, arriving from the other side. A dark theme
 * does not merely restyle this mark — it reverses the measurement that chose its colour.
 *
 * <p>So the choice is COMPUTED from the surface the mark sits on, by the same {@link markOn} the
 * icon plates use. One rule, four answers, none of them stored.
 *
 * @param {string} key  'long' for PTA, anything else for PTB
 * @param {string} mode MUI's `palette.mode`. Absent is treated as light, which is what every
 *                      caller written before the dark theme existed was implicitly asking for.
 */
export function cycleHandleColors(key, mode) {
  const hex = cycleHex(key);
  // The surface the diamond is actually ON: the channel's own fill, composited over the canvas.
  // Not the canvas and not the full-strength hue -- the handle sits INSIDE the band, between its
  // rays, so the tint is what it has to stand against rather than either colour producing it.
  const tint = compositeOver(hex, CYCLE_FILL_ALPHA, canvasFor(mode));
  return {
    /** The core, chosen by contrast against the band's own fill on this theme's canvas. */
    fill: tint ? markOn(tint) : CYCLE_INK,
    /** The rim — the band's own hue, which is the whole of the handle's identity. */
    border: hex,
  };
}

/**
 * The handle's border width and diameter.
 *
 * <p>Eleven rather than the nine it shipped at, because a two-pixel rim on a nine-pixel diamond
 * leaves five pixels of core and the mark stops reading as a gem with a ring. The larger symbol is
 * also a larger hit area, which is the complaint that produced this change.
 */
export const CYCLE_HANDLE_BORDER_WIDTH = 2;
export const CYCLE_HANDLE_SIZE = 11;
